import { useCallback, useRef, useState, type KeyboardEvent, type PointerEvent } from 'react'

export const STEP_PX = 16

/** A window the reader can move out of the way (the Map's selected-place
 *  card, Sensor info's Edit properties dialog): dragged by its title or
 *  grip with the pointer, or moved 16 px per arrow key from the grip.
 *  Pointer moves are divided by the UI zoom, since they arrive in screen
 *  pixels and the offset is in the zoomed page's own. */
export function useMovable() {
  const [offset, setOffset] = useState({ x: 0, y: 0 })
  const start = useRef<{ x: number; y: number; ox: number; oy: number } | null>(null)

  const zoom = () => document.body.currentCSSZoom || 1

  const onPointerDown = useCallback((event: PointerEvent<HTMLElement>) => {
    if (event.button !== 0) return
    event.preventDefault()
    event.currentTarget.setPointerCapture(event.pointerId)
    start.current = { x: event.clientX, y: event.clientY, ox: offset.x, oy: offset.y }
  }, [offset])

  const onPointerMove = useCallback((event: PointerEvent<HTMLElement>) => {
    const from = start.current
    if (!from) return
    const z = zoom()
    setOffset({ x: from.ox + (event.clientX - from.x) / z, y: from.oy + (event.clientY - from.y) / z })
  }, [])

  const onPointerUp = useCallback(() => { start.current = null }, [])

  const onKeyDown = useCallback((event: KeyboardEvent<HTMLElement>) => {
    const move = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] }[
      event.key] as [number, number] | undefined
    if (!move) return
    event.preventDefault()
    setOffset((o) => ({ x: o.x + move[0] * STEP_PX, y: o.y + move[1] * STEP_PX }))
  }, [])

  const drag = { onPointerDown, onPointerMove, onPointerUp, onPointerCancel: onPointerUp }
  return {
    style: { transform: `translate(${offset.x}px, ${offset.y}px)` },
    handle: drag,
    grip: { ...drag, onKeyDown, title: 'Drag to move, or use the arrow keys' },
    reset: () => setOffset({ x: 0, y: 0 }),
  }
}
